import React, { useState, useRef, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress } from "reactstrap";
import { useDropzone } from "react-dropzone";

// Import SVG icons
import { ReactComponent as ImageIcon } from "assets/icons/image.svg";
import { ReactComponent as FileColorIcon } from "assets/icons/file-color.svg";
import { ReactComponent as CheckCircleIcon } from "assets/icons/check-circle.svg";
import { ReactComponent as DownloadIcon } from "assets/icons/download.svg";
import { ReactComponent as TrashIcon } from "assets/icons/trash.svg";

const UploadMultiple = ({
  isOpen,
  toggle,
  initFiles = [], // array url or file
  title = "อัปโหลดรูปภาพ",
  desc = "อัปโหลดรูปภาพได้ที่ด้านล่างนี้",
  message = "อัพโหลดรูปภาพ",
  accept = "PNG, JPG, JPEG, WEBP",
  maxFile = 0,
  maxSize = 5 * 1024 * 1024, // 5MB default
  onUploadFile = null,
  onRemoveFile = null,
  onConfirm,
  onCancel,
}) => {
  const [files, setFiles] = useState(() =>
    initFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      url: typeof file === "string" ? file : URL.createObjectURL(file),
      name: typeof file === "string" ? file.split("/").pop() : file.name,
      size: typeof file === "string" ? "" : formatFileSize(file.size),
      status: "completed",
      progress: 100,
    }))
  );

  const handleFiles = (newFiles) => {
    // ตรวจสอบจำนวนไฟล์
    if (maxFile > 0 && files.length + newFiles.length > maxFile) {
      alert(`สามารถอัปโหลดได้สูงสุด ${maxFile} ไฟล์เท่านั้น`);
      return;
    }

    // ตรวจสอบขนาดไฟล์
    const oversizedFiles = newFiles.filter((file) => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      alert(`ไฟล์ต้องมีขนาดไม่เกิน ${formatFileSize(maxSize)} ต่อไฟล์`);
      return;
    }

    const processedFiles = newFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: formatFileSize(file.size),
      progress: 0,
      status: "uploading",
      url: null,
    }));

    setFiles((prev) => {
      const updatedFiles = [...prev, ...processedFiles];
      processedFiles.forEach((fileData) => {
        if (onUploadFile && typeof onUploadFile === "function") {
          handleUpload(fileData);
        } else {
          simulateUpload(fileData.id);
        }
      });
      return updatedFiles;
    });
  };

  const handleDrop = useCallback((acceptedFiles) => {
    handleFiles(acceptedFiles);
  }, []);

  const handleInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": accept.split(",").map((type) => "." + type.trim().toLowerCase()),
    },
    noClick: true,
    maxFiles: maxFile > 0 ? maxFile : undefined,
    maxSize: maxSize,
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach((rejection) => {
        if (rejection.errors[0].code === "file-too-large") {
          alert(`ไฟล์ ${rejection.file.name} มีขนาดใหญ่เกินไป (สูงสุด ${formatFileSize(maxSize)})`);
        } else if (rejection.errors[0].code === "too-many-files") {
          alert(`สามารถอัปโหลดได้สูงสุด ${maxFile} ไฟล์เท่านั้น`);
        }
      });
    },
  });

  const handleUpload = async (fileData) => {
    if (!fileData || !fileData.file) return;
    try {
      const onProgress = (progress) => {
        setFiles((prev) => prev.map((f) => (f.id === fileData.id ? { ...f, progress } : f)));
      };

      const url = await onUploadFile(fileData.file, onProgress);

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? {
                ...f,
                progress: 100,
                status: "completed",
                url: url,
              }
            : f
        )
      );
    } catch (error) {
      console.error("Upload error:", error);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? {
                ...f,
                progress: 0,
                status: "error",
                error: "อัพโหลดไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
              }
            : f
        )
      );
    }
  };

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                  ...f,
                  progress: 100,
                  status: "completed",
                  url: URL.createObjectURL(f.file),
                }
              : f
          )
        );
      } else {
        setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, progress } : f)));
      }
    }, 500);
  };

  const handleDelete = (fileId) => {
    const fileToRemove = files.find((f) => f.id === fileId);
    if (fileToRemove) {
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      if (onRemoveFile && typeof onRemoveFile === "function") {
        onRemoveFile(fileToRemove);
      }
    }
  };

  const handleDownload = async (file) => {
    try {
      let url, fileName;

      if (file.file) {
        url = URL.createObjectURL(file.file);
        fileName = file.name || "download";
      } else if (file.url) {
        if (file.url.startsWith("blob:")) {
          // For blob URLs from uploads
          url = file.url;
        } else {
          // For remote URLs
          const response = await fetch(file.url);
          const blob = await response.blob();
          url = URL.createObjectURL(blob);
        }
        fileName = file.name || file.url.split("/").pop() || "download";
      } else {
        throw new Error("No file or URL available");
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      if (url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Download failed:", error);
      alert("ไม่สามารถดาวน์โหลดไฟล์ได้");
    }
  };

  const formatFileSize = (bytes) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Byte";
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered={true} fade={true} className="modal-left" size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <div className="modal-des">{desc}</div>

        <div className="modal-child h-96">
          <input
            type="file"
            onChange={handleInputChange}
            accept={accept
              .split(", ")
              .map((ext) => `.${ext.toLowerCase()}`)
              .join(",")}
            multiple={maxFile > 1}
            style={{ display: "none" }}
            id="file-input"
          />

          <div {...getRootProps()} className="dropzone-upload">
            <input {...getInputProps()} />
            <label
              htmlFor="file-input"
              style={{
                cursor: "pointer",
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ImageIcon style={{ width: 100, height: 100, marginBottom: 16 }} />
              <h3 className="mb-2 fw-semibold">{message}</h3>
              <div className="text-muted small">
                <h6 className="text-primary-600">รองรับชนิดไฟล์ดังนี้: {accept}</h6>
                {maxFile > 0 && (
                  <span className="text-md">
                    จำนวนไฟล์: {files.length}/{maxFile}
                  </span>
                )}
                <br />
                <span className="text-md">ขนาดไฟล์สูงสุด: {formatFileSize(maxSize)}</span>
              </div>
            </label>
          </div>

          {files.length > 0 && (
            <div className="mt-4">
              <h4 className="text-muted mb-3">
                อัปโหลดแล้ว {files.filter((f) => f.status === "completed").length}/{files.length}
              </h4>

              <div className="d-flex flex-column gap-3">
                {files.map((file) => (
                  <div key={file.id} className="dropzone-file-card d-flex align-items-center gap-2 px-3 py-2 rounded-lg w-100 bg-gray-100">
                    <FileColorIcon className="file-icon" />
                    <div className="file-info flex-fill">
                      <div className="file-name d-flex gap-2 align-items-center">
                        <div className="d-flex align-items-end gap-2">
                          <h4 className="fw-semibold">{file.name}</h4>
                          {file.size.length > 0 && <span className="file-size">({file.size})</span>}
                        </div>
                        {file.status === "completed" && (
                          <>
                            <CheckCircleIcon
                              style={{
                                width: 16,
                                height: 16,
                              }}
                            />
                            <button
                              className="icon-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(file);
                              }}
                            >
                              <DownloadIcon style={{ width: 16, height: 16 }} />
                            </button>
                          </>
                        )}
                      </div>
                      <Progress value={file.progress} className="mb-0" />
                    </div>
                    <div>
                      <div className="text-xl fw-semibold w-10 d-flex flex-column align-items-center">
                        <button className="icon-button" onClick={() => handleDelete(file.id)}>
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                        {Math.round(file.progress)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="d-flex justify-content-between gap-3 w-100">
          <Button color="light" outline className="w-100 max-w-52" onClick={onCancel}>
            ยกเลิก
          </Button>
          <Button className="w-100 max-w-52" color="primary" onClick={() => onConfirm(files)}>
            เสร็จสิ้น
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default UploadMultiple;
